import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types/user';

export async function getOrganizationUsers(organizationId: string): Promise<User[]> {
  const { data, error } = await supabase
    .from('organization_members')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching organization users:', error);
    throw error;
  }

  return (data || []).map(member => ({
    id: member.id,
    name: member.full_name || member.email,
    email: member.email,
    role: (member.role.charAt(0).toUpperCase() + member.role.slice(1)) as 'Admin' | 'Editor' | 'Viewer',
    status: member.is_active ? 'Active' : 'Inactive',
    lastActive: member.updated_at,
    invitationStatus: member.invitation_status as 'pending' | 'accepted' | 'expired',
    invitedAt: member.invited_at,
    invitedBy: member.invited_by,
    user_type: member.user_type as 'org_admin' | 'team_member',
    organization_id: member.organization_id,
    organization_name: undefined // Will be populated if needed
  }));
}

export async function createOrganizationMember(memberData: {
  id: string;
  email: string;
  full_name?: string;
  organization_id: string;
  role?: string;
  user_type?: 'org_admin' | 'team_member';
  invited_by?: string;
}) {
  const { data, error } = await supabase
    .from('organization_members')
    .insert([{
      id: memberData.id,
      email: memberData.email,
      full_name: memberData.full_name,
      organization_id: memberData.organization_id,
      role: memberData.role || 'viewer',
      user_type: memberData.user_type || 'team_member',
      invited_by: memberData.invited_by,
      invitation_status: 'pending'
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating organization member:', error);
    throw error;
  }

  return data;
}

export async function updateOrganizationMember(id: string, updates: {
  full_name?: string;
  role?: string;
  user_type?: string;
  is_active?: boolean;
  invitation_status?: string;
}) {
  const { data, error } = await supabase
    .from('organization_members')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating organization member:', error);
    throw error;
  }

  return data;
}

export async function deleteOrganizationMember(id: string) {
  // First, set all invited_by references to NULL for users invited by this member
  const { error: updateError } = await supabase
    .from('organization_members')
    .update({ invited_by: null })
    .eq('invited_by', id);

  if (updateError) {
    console.error('Error updating invited_by references:', updateError);
    throw updateError;
  }

  // Now delete the organization member
  const { error } = await supabase
    .from('organization_members')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting organization member:', error);
    throw error;
  }
}

export async function getOrganizationDetails(organizationId: string) {
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', organizationId)
    .single();

  if (error) {
    console.error('Error fetching organization details:', error);
    throw error;
  }

  return data;
}
